import LogicFlow, {
  HtmlNode,
  HtmlNodeModel,
  Model,
  h,
  BaseEdgeModel,
} from '@logicflow/core';

// 补充缺失的类型定义
interface AnchorInfo {
  id: string;
  x: number;
  y: number;
  // 其他可能的属性
}

interface Edge extends BaseEdgeModel {
  updatePathByAnchor: () => void;
  // 保留其他必要属性
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
}
// 定义字段数据结构接口
interface Field {
  key: string;
  type: string;
  id: string;
}

// 定义SqlNodeModel属性接口
interface SqlNodeProperties {
  fields: Field[];
  tableName: string;
  isConnection?: boolean;
  [key: string]: any;
}

class SqlNode extends HtmlNode {
  /**
   * 重写锚点形状 - 显示为圆形的点
   */
  getAnchorShape(anchorData?: Model.AnchorConfig) {
    if (!anchorData) {
      return null;
    }
    const { x, y, type } = anchorData;
    return h('circle', {
      cx: x,
      cy: y,
      r: 5,
      className: `custom-anchor ${type === 'left' ? 'incomming-anchor' : 'outgoing-anchor'}`,
      fill: type === 'left' ? 'red' : 'green',
      stroke: '#fff',
      strokeWidth: 2,
    });
  }

  setHtml(rootEl: SVGForeignObjectElement) {
    rootEl.innerHTML = '';
    const {
      isItOntology,
      properties: { fields, tableName },
    } = this.props.model as unknown as { isItOntology: boolean, properties: SqlNodeProperties };

    rootEl.setAttribute('class', 'table-container');
    const container = document.createElement('div');
    
    // 根据表名设置不同的背景色
    if (isItOntology) {
      container.className = 'table-node table-color-ontology';
    } else {
      container.className = 'table-node table-color-white';
    }

    const tableNameElement = document.createElement('div');
    tableNameElement.innerText = tableName as string;
    tableNameElement.className = 'table-name';
    // 使用原生title属性显示完整文本
    tableNameElement.setAttribute('title', tableName as string);
    container.appendChild(tableNameElement);

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < fields.length; i++) {
      const item = fields[i];
      const itemElement = document.createElement('div');
      itemElement.className = 'table-feild';

      const itemKey = document.createElement('span');
      itemKey.innerText = item.key;
      // 使用原生title属性显示完整字段名
      itemKey.setAttribute('title', item.key);

      const itemType = document.createElement('span');
      itemType.innerText = item.type;
      itemType.className = 'feild-type';
      // 使用原生title属性显示完整类型
      itemType.setAttribute('title', item.type);

      itemElement.appendChild(itemKey);
      itemElement.appendChild(itemType);
      fragment.appendChild(itemElement);
    }

    container.appendChild(fragment);
    rootEl.appendChild(container);
  }
}

class SqlNodeModel extends HtmlNodeModel {
  properties: SqlNodeProperties;

  constructor(data: any, graphModel: any) {
    super(data, graphModel);
    // 初始化属性默认值
    this.properties = {
      fields: data.properties?.fields || [],
      tableName: data.properties?.tableName || '',
      isConnection: data.properties?.isConnection || false,
      isItOntology: data.properties?.isItOntology || false,
    };
  }

  /**
   * 给model自定义添加字段方法
   */
  addField(item: Field) {
    this.properties.fields.unshift(item);
    this.setAttributes();
    // 调整节点位置
    this.move(0, 24 / 2);
    // 更新节点连接边的path
    (this.incoming.edges as Edge[]).forEach(edge => {
      edge.updatePathByAnchor();
    });
    (this.outgoing.edges as Edge[]).forEach(edge => {
      edge.updatePathByAnchor();
    });
  }

  getOutlineStyle() {
    const style = super.getOutlineStyle();
    style.stroke = 'none';
    if (style.hover) style.hover.stroke = 'none';
    return style;
  }

  getAnchorStyle(anchorInfo: Model.AnchorConfig) {
    const style = super.getAnchorStyle(anchorInfo);
    if (anchorInfo.type === 'left') {
      style.fill = 'red';
      if (style.hover) {
        style.hover.fill = 'transparent';
        style.hover.stroke = 'transparent';
      }
      style.className = 'lf-hide-default';
    } else {
      style.fill = 'green';
    }
    return style;
  }

  setAttributes() {
    this.width = 200;
    const { fields } = this.properties;
    this.height = 60 + fields.length * 24;

    const circleOnlyAsTarget = {
      message: '只允许从右边的锚点连出',
      validate: (
        sourceNode: any,
        targetNode: any,
        sourceAnchor?: Model.AnchorConfig
      ) => {
        return sourceAnchor?.type === 'right';
      },
    };

    // 阻止表级锚点连接到本体节点（但允许字段级锚点连接）
    const preventTableLevelOntologyConnection = {
      message: '表级锚点不能连接到本体节点，请使用字段级锚点进行映射',
      validate: (
        sourceNode: any,
        targetNode: any,
        sourceAnchor?: Model.AnchorConfig,
        targetAnchor?: Model.AnchorConfig
      ) => {
        // 检查目标节点是否是本体节点
        const isTargetOntology = targetNode?.properties?.isItOntology;
        // 检查源节点是否是本体节点
        const isSourceOntology = sourceNode?.properties?.isItOntology;
        
        // 如果涉及本体节点
        if (isTargetOntology || isSourceOntology) {
          // 检查是否是表级锚点（ID包含 _table_）
          const isSourceTableAnchor = sourceAnchor?.id?.includes('_table_');
          const isTargetTableAnchor = targetAnchor?.id?.includes('_table_');
          
          // 如果是表级锚点连接本体节点，则阻止
          if (isSourceTableAnchor || isTargetTableAnchor) {
            return false;
          }
          // 字段级锚点可以连接本体节点
          return true;
        }
        return true;
      },
    };

    this.sourceRules.push(circleOnlyAsTarget);
    this.sourceRules.push(preventTableLevelOntologyConnection);
    
    this.targetRules.push({
      message: '只允许连接左边的锚点',
      validate: (
        sourceNode: any,
        targetNode: any,
        sourceAnchor?: Model.AnchorConfig,
        targetAnchor?: Model.AnchorConfig
      ) => {
        return targetAnchor?.type === 'left';
      },
    });
    this.targetRules.push(preventTableLevelOntologyConnection);
  }

  getDefaultAnchor(): Model.AnchorConfig[] {
    const {
      id,
      x,
      y,
      width,
      height,
      isHovered,
      isSelected,
      properties: { fields, isConnection },
    } = this;

    const anchors: Model.AnchorConfig[] = [];
    
    // 为整个表创建锚点（表级别连接）
    // 左侧锚点（表的顶部中间位置 - 进线位置）
    if (isConnection || !(isHovered || isSelected)) {
      anchors.push({
        x: x, // 表的水平中间位置
        y: y - height / 2 + 10, // 表的顶部位置
        id: `${id}_table_left`,
        edgeAddable: true,
        type: 'left',
      });
    }
    // 右侧锚点（表的底部中间位置 - 出线位置）
    if (!isConnection) {
      anchors.push({
        x: x, // 表的水平中间位置
        y: y + height / 2 - 10, // 表的底部位置
        id: `${id}_table_right`,
        type: 'right',
      });
    }
    
    // 为每个字段创建锚点（字段级别连接）
    fields.forEach((field, index) => {
      // 左侧锚点
      if (isConnection || !(isHovered || isSelected)) {
        anchors.push({
          x: x - width / 2 + 10,
          y: y - height / 2 + 60 + index * 24,
          id: `${id}_${field.key}_left`,
          edgeAddable: false,
          type: 'left',
        });
      }
      // 右侧锚点
      if (!isConnection) {
        anchors.push({
          x: x + width / 2 - 10,
          y: y - height / 2 + 60 + index * 24,
          id: `${id}_${field.key}_right`,
          type: 'right',
        });
      }
    });

    return anchors;
  }
}
export default {
  type: 'sql-node',
  model: SqlNodeModel,
  view: SqlNode,
};
